import { useEffect, useRef, type RefObject } from 'react';
import type {
  DuetChangeEvent,
  DuetdsDateAdapter,
  DuetDatePickerAttributes,
  DuetDatePickerElement,
} from './duet-date-picker';
import type { DuetLocalization } from './datepickerLocalization';

export type {
  DuetChangeEvent,
  DuetChangeEventDetail,
  DuetdsDateAdapter,
} from './duet-date-picker';
export type { DuetLocalization } from './datepickerLocalization';

export interface DuetdsWrapperProps extends Omit<
  DuetDatePickerAttributes,
  'ref' | 'identifier' | 'onChange' | 'onFocus' | 'onBlur'
> {
  id: string;
  onChange?: (event: DuetChangeEvent | null) => void;
  onFocus?: (event: Event) => void;
  onBlur?: (event: Event) => void;
  onOpen?: (event: Event) => void;
  onClose?: (event: Event) => void;
  dateAdapter?: DuetdsDateAdapter;
  localization?: DuetLocalization;
}

function useListener(
  ref: RefObject<DuetDatePickerElement | null>,
  eventName: string,
  handler?: (event: Event) => void
) {
  useEffect(() => {
    const element = ref.current;
    if (!element || !handler) {
      return undefined;
    }
    const listener: EventListener = (event) => {
      handler(event);
    };
    element.addEventListener(eventName, listener);
    return () => element.removeEventListener(eventName, listener);
  }, [eventName, handler, ref]);
}

function DuetdsWrapper({
  onChange,
  onFocus,
  onBlur,
  onOpen,
  onClose,
  dateAdapter,
  localization,
  id,
  ...props
}: DuetdsWrapperProps) {
  const ref = useRef<DuetDatePickerElement>(null);

  useListener(ref, 'duetChange', (event) => {
    if (!onChange || !('detail' in event)) {
      return;
    }
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- duet custom event detail
    onChange(event as DuetChangeEvent);
  });
  useListener(ref, 'duetFocus', onFocus);
  useListener(ref, 'duetBlur', onBlur);
  useListener(ref, 'duetOpen', onOpen);
  useListener(ref, 'duetClose', onClose);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }
    if (localization !== undefined) {
      element.localization = localization;
    }
    if (dateAdapter !== undefined) {
      element.dateAdapter = dateAdapter;
    }
  }, [localization, dateAdapter]);

  // oxlint-disable-next-line react/jsx-props-no-spreading
  return <duet-date-picker ref={ref} identifier={id} {...props} />;
}

export default DuetdsWrapper;
